import { useCallback, useEffect, useState } from 'react';

import { Alert, Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconMessageCircle,
  IconRefresh,
} from '@tabler/icons-react';

import { useSubjectCandidates } from '@/hooks';
import { useReportFeedback } from '@/hooks/useReportFeedback';
import { SubjectOverrides } from '@/types/subject-candidate';
import { SubjectCandidateCard } from './SubjectCandidateCard';
import classes from './SubjectIdentification.module.css';

interface SubjectIdentificationProps {
  reportId: string;
  /**
   * The report already existed and feedback re-ran identification, rather
   * than this being first-time identification. Shows the feedback that
   * triggered the re-run: without it an analyst ranks candidates on
   * evidence alone, which picks the wrong entity whenever the correct one
   * is newly registered and therefore thinly sourced.
   */
  fromFeedback?: boolean;
  /**
   * Called when the whole report has advanced past identification (every
   * subject resolved). The parent typically re-fetches the report.
   */
  onResolved?: () => void;
  /**
   * Called when identification fails terminally (identification_exhausted).
   * The parent typically shows the report's failure view.
   */
  onFailed?: () => void;
}

export function SubjectIdentification({
  reportId,
  fromFeedback = false,
  onResolved,
  onFailed,
}: Readonly<SubjectIdentificationProps>) {
  const { subjects, isLoading, isSubmitting, error, isFromCache, refetch, submit } =
    useSubjectCandidates(reportId);
  const [refreshing, setRefreshing] = useState(false);

  const { feedbackHistory, fetchFeedback } = useReportFeedback();
  useEffect(() => {
    if (fromFeedback && reportId) {
      void fetchFeedback(reportId);
    }
  }, [fromFeedback, reportId, fetchFeedback]);
  // Newest first — the correction being acted on is the most recent one.
  const latestFeedback = feedbackHistory.length
    ? [...feedbackHistory].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))[0]
    : null;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const handleSelection = useCallback(
    async (subjectId: string, candidateId: string, overrides?: SubjectOverrides) => {
      try {
        const response = await submit(subjectId, {
          selected_candidate_id: candidateId,
          ...(overrides ? { overrides } : {}),
        });
        if (response.next_action === 'triggered:data_acquisition') {
          // Admins read the real stage; clients get the coarse one.
          const resumed =
            response.report_status === 'processing' || response.report_status === 'in_progress';
          notifications.show({
            color: 'green',
            title: 'Subject confirmed',
            message: resumed
              ? 'Research is resuming.'
              : 'Remaining subjects still need confirmation.',
          });
          if (resumed) {
            onResolved?.();
          }
        } else if (response.next_action === 'triggered:feedback_rerun') {
          // The correction path: the report already existed, so the whole
          // pipeline re-runs for this subject from wherever it had work.
          notifications.show({
            color: 'green',
            title: 'Subject corrected',
            message: 'The report is being rebuilt for the confirmed subject.',
          });
          onResolved?.();
        } else if (response.next_action === 'waiting') {
          // The selection saved but no phase fired — say so rather than
          // implying progress the report isn't making.
          notifications.show({
            color: 'yellow',
            title: 'Subject confirmed, but nothing resumed',
            message: 'The selection was saved. This report needs a manual check.',
          });
          onResolved?.();
        } else if (response.next_action === 'report_failed') {
          onFailed?.();
        }
      } catch {
        // errors surfaced via hook `error` state; notify for visibility
        notifications.show({
          color: 'red',
          title: 'Selection failed',
          message: 'Could not submit selection. Please try again.',
        });
      }
    },
    [submit, onResolved, onFailed]
  );

  const handleClarification = useCallback(
    async (subjectId: string, additionalContext: string) => {
      try {
        const response = await submit(subjectId, { additional_context: additionalContext });
        if (response.next_action === 'triggered:subject_identification') {
          notifications.show({
            color: 'blue',
            title: 'Re-identifying subject',
            message: 'We’ll refresh the candidates in a moment.',
          });
        } else if (response.next_action === 'report_failed') {
          onFailed?.();
        }
      } catch {
        notifications.show({
          color: 'red',
          title: 'Clarification failed',
          message: 'Could not submit clarification. Please try again.',
        });
      }
    },
    [submit, onFailed]
  );

  if (isLoading && subjects.length === 0) {
    return (
      <Stack align="center" gap="sm" py="xl">
        <Loader size="md" />
        <Text size="sm" c="dimmed">
          Loading candidate subjects...
        </Text>
      </Stack>
    );
  }

  if (error && subjects.length === 0) {
    return (
      <Alert variant="light" color="red" icon={<IconAlertCircle size={16} />}>
        Failed to load candidates: {error}
      </Alert>
    );
  }

  if (subjects.length === 0) {
    return (
      <Alert variant="light" color="blue" icon={<IconAlertCircle size={16} />}>
        No subjects to identify for this report.
      </Alert>
    );
  }

  const allResolved = subjects.every((s) => s.is_resolved);

  return (
    <Stack gap="lg" className={classes.root}>
      <Stack gap={4}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Title order={3}>
            {fromFeedback ? 'Confirm the corrected subject' : 'Confirm subject identity'}
          </Title>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
        </Group>
        <Text size="sm" c="dimmed">
          {fromFeedback
            ? 'Feedback on this report re-ran identification. Pick the candidate the feedback describes, or add more context if none of them match — the report is rebuilt for whichever subject you confirm.'
            : 'We found multiple potential matches. Select the right candidate for each subject, or provide more context if none match. The report will resume once every subject is confirmed.'}
        </Text>
        {isFromCache && !refreshing && (
          <Text size="xs" c="dimmed">
            Showing previously loaded results. Use Refresh to check for updates.
          </Text>
        )}
      </Stack>

      {error && (
        <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={16} />}>
          {error}
        </Alert>
      )}

      {fromFeedback && latestFeedback && (
        <Alert
          variant="light"
          color="blue"
          icon={<IconMessageCircle size={16} />}
          title="The feedback that triggered this"
        >
          <Stack gap={4}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {latestFeedback.feedback}
            </Text>
            {latestFeedback.context && (
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                On: {latestFeedback.context}
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      <Stack gap="md">
        {subjects.map((subject) => (
          <SubjectCandidateCard
            key={subject.subject_id}
            subject={subject}
            disabled={isSubmitting}
            onSelectCandidate={handleSelection}
            onSubmitClarification={handleClarification}
          />
        ))}
      </Stack>

      {allResolved && (
        <Alert variant="light" color="green" icon={<IconAlertCircle size={16} />}>
          All subjects confirmed. Resuming research...
        </Alert>
      )}
    </Stack>
  );
}
